<template>
  <Teleport to="body">
    <div
      v-if="isOpen"
      class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4 animate-fade-in"
      @click.self="handleClose"
    >
      <div 
        class="bg-white rounded-2xl shadow-2xl w-full overflow-hidden transform transition-all duration-300 animate-scale-in border border-gray-200/50"
        :class="sizeClasses"
      >
        <!-- 头部 -->
        <div 
          v-if="showHeader"
          class="flex justify-between items-center p-4 sm:p-6 border-b border-gray-200/80 bg-gradient-to-r from-gray-50 to-white"
        >
          <div class="flex items-center gap-3">
            <!-- 图标 -->
            <div 
              v-if="icon"
              class="w-8 h-8 rounded-lg flex items-center justify-center"
              :class="iconClasses"
            >
              <component :is="icon" class="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 class="text-xl sm:text-2xl font-bold text-gray-900">
                {{ title }}
              </h2>
              <p v-if="subtitle" class="text-sm sm:text-base text-gray-600 mt-1">
                {{ subtitle }}
              </p>
            </div>
          </div>
          <button
            v-if="showCloseButton"
            @click="handleClose"
            class="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-2 rounded-full transition-all duration-200 group"
          >
            <X class="w-5 h-5 sm:w-6 sm:h-6 group-hover:rotate-90 transition-transform duration-200" />
          </button>
        </div>

        <!-- 内容区域 -->
        <div class="flex-1 overflow-auto" :class="contentClasses">
          <slot />
        </div>

        <!-- 底部 -->
        <div 
          v-if="showFooter"
          class="flex justify-end gap-3 p-4 sm:p-6 border-t border-gray-200/80 bg-gray-50/50"
        >
          <slot name="footer">
            <button
              v-if="showCancelButton"
              @click="handleClose"
              class="btn-secondary"
            >
              {{ cancelText }}
            </button>
            <button
              v-if="showConfirmButton"
              @click="handleConfirm"
              class="btn-primary"
              :disabled="confirmDisabled"
            >
              {{ confirmText }}
            </button>
          </slot>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { X } from 'lucide-vue-next'

interface Props {
  isOpen: boolean
  title?: string
  subtitle?: string
  icon?: any
  iconColor?: 'red' | 'blue' | 'green' | 'purple' | 'yellow' | 'gray'
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full'
  showHeader?: boolean
  showFooter?: boolean
  showCloseButton?: boolean
  showCancelButton?: boolean
  showConfirmButton?: boolean
  cancelText?: string
  confirmText?: string
  confirmDisabled?: boolean
  closeOnClickOutside?: boolean
}

interface Emits {
  (e: 'close'): void
  (e: 'confirm'): void
}

const props = withDefaults(defineProps<Props>(), {
  title: '',
  subtitle: '',
  iconColor: 'blue',
  size: 'lg',
  showHeader: true,
  showFooter: false,
  showCloseButton: true,
  showCancelButton: false,
  showConfirmButton: false,
  cancelText: '取消',
  confirmText: '确认',
  confirmDisabled: false,
  closeOnClickOutside: true,
})

const emit = defineEmits<Emits>()

const sizeClasses = computed(() => {
  const sizes = {
    sm: 'max-w-md',
    md: 'max-w-2xl',
    lg: 'max-w-4xl max-h-[90vh]',
    xl: 'max-w-6xl max-h-[95vh]',
    full: 'h-full sm:max-w-7xl sm:max-h-[95vh] sm:h-auto'
  }
  return sizes[props.size]
})

const iconClasses = computed(() => {
  const colors = {
    red: 'bg-gradient-to-br from-red-500 to-red-600',
    blue: 'bg-gradient-to-br from-blue-500 to-blue-600',
    green: 'bg-gradient-to-br from-green-500 to-green-600',
    purple: 'bg-gradient-to-br from-purple-500 to-purple-600',
    yellow: 'bg-gradient-to-br from-yellow-500 to-yellow-600',
    gray: 'bg-gradient-to-br from-gray-500 to-gray-600'
  }
  return colors[props.iconColor]
})

const contentClasses = computed(() => {
  if (props.size === 'full') {
    return 'h-[calc(100vh-140px)] sm:h-[calc(90vh-140px)]'
  }
  return 'max-h-[70vh]'
})

const handleClose = () => {
  if (props.closeOnClickOutside) {
    emit('close')
  }
}

const handleConfirm = () => {
  emit('confirm')
}
</script>
